'use client';

import type { ReactNode } from 'react';
import { Card, CardBody } from '@heroui/card';
import { Star } from 'lucide-react';

interface FeatureProps {
  title?: string;
  description?: JSX.Element | string;
  variant?:
    | 'simple'
    | 'timeline'
    | 'card'
    | 'horizontal'
    | 'definition'
    | 'bordered'
    | 'numbered'
    | 'howItWorks';
  showIcon?: boolean;
  icon?: JSX.Element | string;
  index?: number;
  className?: string; // Added className prop
}

export function Feature({
  title,
  description,
  variant = 'simple',
  showIcon = true,
  icon = <Star className="w-6 h-6" />,
  index = 0,
  className = '', // Default to an empty string
}: FeatureProps) {
  if (!title) return null;

  const variants = {
    simple: (
      <div className={`flex flex-col items-center text-center group ${className}`}>
        {showIcon && (
          <div className="flex items-center justify-center w-16 h-16 mb-4 transition-colors duration-300 rounded-full bg-primary/10 group-hover:bg-primary/20">
            {icon}
          </div>
        )}
        <h3 className="mb-2 text-lg font-semibold">{title}</h3>
        {description && <p className="text-sm text-foreground/70">{description}</p>}
      </div>
    ),

    timeline: (
      <div className={`relative ${className}`}>
        <div className="flex items-center mb-4">
          {showIcon && (
            <div className="z-10 flex items-center justify-center w-12 h-12 rounded-full shadow-lg min-w-12 bg-primary">
              {icon}
            </div>
          )}
          <div className="flex-grow p-4 ml-4 border rounded-lg border-primary/20 bg-background">
            <h3 className="mb-2 text-lg font-semibold">{title}</h3>
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
        </div>
      </div>
    ),

    card: (
      <div
        className={`flex flex-col items-center p-6 text-center transition-colors duration-300 rounded-lg bg-primary/5 group hover:bg-primary/10 ${className}`}
      >
        {showIcon && (
          <div className="flex items-center justify-center w-16 h-16 mb-4 transition-colors duration-300 rounded-full bg-primary/20 group-hover:bg-primary/30">
            {icon}
          </div>
        )}
        <h3 className="mb-2 text-lg font-semibold">{title}</h3>
        {description && <p className="text-sm text-foreground/70">{description}</p>}
      </div>
    ),

    horizontal: (
      <div className={`flex items-start space-x-4 group ${className}`}>
        {showIcon && (
          <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 transition-colors duration-300 rounded-full bg-primary/10 group-hover:bg-primary/20">
            {icon}
          </div>
        )}
        <div>
          <h3 className="mb-2 text-lg font-semibold">{title}</h3>
          {description && <p className="text-sm text-foreground/70">{description}</p>}
        </div>
      </div>
    ),

    definition: (
      <div className={`flex flex-col ${className}`}>
        <dt className="flex items-center text-lg font-semibold leading-7 gap-x-3">
          {showIcon && icon}
          {title}
        </dt>
        {description && (
          <dd className="flex flex-col flex-auto mt-4 text-sm leading-7 text-foreground/70">
            <p className="text-sm text-foreground/70">{description}</p>
          </dd>
        )}
      </div>
    ),

    bordered: (
      <div className={`p-6 border rounded-lg shadow-sm border-divider ${className}`}>
        <div className="flex gap-4 group">
          {showIcon && (
            <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 transition-colors duration-300 rounded-full bg-primary/10 group-hover:bg-primary/20">
              {icon}
            </div>
          )}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">{title}</h3>
            {description && <p className="text-sm text-foreground/70">{description}</p>}
          </div>
        </div>
      </div>
    ),

    numbered: (
      <Card className={className}>
        <CardBody className="p-6 space-y-7">
          <div className="flex items-center justify-center w-10 h-10 mb-4 text-lg font-bold rounded-full bg-primary text-primary-foreground">
            {showIcon ? icon : index + 1}
          </div>
          <div>
            <h3 className="mb-2 text-lg font-semibold">{title}</h3>
            {description && <p className="text-sm text-foreground/70">{description}</p>}
          </div>
        </CardBody>
      </Card>
    ),

    howItWorks: (
      <Card
        className={`relative md:w-5/12 ${
          index % 2 === 0 ? 'md:ml-auto md:mr-8' : 'md:mr-auto md:ml-8'
        } ${className}`}
      >
        <div className="p-6">
          <div className="flex items-start gap-4 mb-4">
            {showIcon && <div className="p-2 rounded-lg bg-primary/10">{icon}</div>}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">{title}</h3>
              {description && <p className="text-sm text-foreground/70">{description}</p>}
            </div>
          </div>
        </div>
      </Card>
    ),
  };

  return variants[variant];
}
